import { SUPABASE_URL } from '../utils/supabase/url';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';

import type { GoogleDriveFile, MetadataProjectionState } from '../types/documents';
import { EDITABLE_DOCUMENT_METADATA_KEYS, NATIVE_EDITABLE_DOCUMENT_METADATA_KEYS, type DocumentControlMetadataInput } from '../document-control/metadata';

export interface ReviewedFileMetadata {
  file: GoogleDriveFile;
  metadataRevision?: string;
  projectionState?: MetadataProjectionState;
  supportsMetadataClearing?: boolean;
  supportsConditionalMetadataUpdate?: boolean;
  metadataContractVersion?: number;
  canEdit?: boolean;
}
export class MetadataOutcomeUnknownError extends Error {}
export class MetadataBaselineRefreshRequiredError extends Error {
  constructor(public readonly file: GoogleDriveFile) {
    super('Document refreshed. Review its details and choose the category again.');
  }
}

export function changedDocumentMetadata(original: DocumentControlMetadataInput, edited: DocumentControlMetadataInput, native = false) {
  const documentControlMetadata: DocumentControlMetadataInput = {};
  const clearFields: string[] = [];
  for (const key of native ? NATIVE_EDITABLE_DOCUMENT_METADATA_KEYS : EDITABLE_DOCUMENT_METADATA_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(edited, key)) continue;
    const next = String(edited[key] ?? '').trim();
    if (next === String(original[key] ?? '').trim()) continue;
    if (next) documentControlMetadata[key] = next;
    else clearFields.push(key);
  }
  return { documentControlMetadata, clearFields };
}

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;

export interface ProjectFolders {
  root: string;
  documents: string;
  drawings: string;
  specifications: string;
  contracts: string;
  disputes: string;
  other: string;
  tasks: string;
  boq: string;
  variations: string;
  paymentApplications: string;
  rfis: string;
  cvis: string;
  eots: string;
  contractCompliance?: string;
  commercial?: string;
  claims?: string;
  communications?: string;
  closeout?: string;
  disputeResolution?: string;
  noticesReferrals?: string;
  submissionsPleadings?: string;
  evidenceSupporting?: string;
  adjudicatorDecision?: string;
  settlementAgreements?: string;
  internalConfidential?: string;
  // Dynamic folders created during workflows
  preContract?: string;
  tenderDocuments?: string;
  tenderReview?: string;
  tenderSubmission?: string;
  takeOffs?: string;
  // Legacy snake_case support
  pre_contract?: string;
  tender_submission?: string;
}

export class ProjectFoldersService {
  /**
   * Create folder structure for a project
   * If clientId is provided, creates the project inside the client's "20 - Projects" folder
   */
  async createProjectFolders(
    projectId: string,
    projectName: string,
    userId: string,
    clientId?: string
  ): Promise<{ success: boolean; folders: ProjectFolders; alreadyExists: boolean }> {
    // Retry configuration - folder creation can take a very long time (60+ folders)
    const maxRetries = 3;
    const baseTimeout = 120000; // 2 minutes base timeout (was 60s)
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const controller = new AbortController();
      const timeout = baseTimeout + (attempt * 60000); // Increase timeout with each retry (add 1 min per retry)
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      if (attempt > 0) {
        console.log(`🔄 [ProjectFoldersService] Retry attempt ${attempt}/${maxRetries} with ${timeout/1000}s timeout...`);
      }

      try {
        const response = await fetch(`${API_BASE}/project-folders/${projectId}/create`, {
          method: 'POST',
          headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            userId,
            projectName,
            clientId,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const error = await response.text();
          
          // Check for retryable errors
          const isRetryable = response.status >= 500 || error.includes('timeout') || error.includes('Temporarily unavailable');
          
          if (isRetryable && attempt < maxRetries) {
            const delay = 1000 * (attempt + 1); // Linear backoff: 1s, 2s, 3s
            console.warn(`⚠️ [ProjectFoldersService] Retryable error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          throw new Error(`Failed to create project folders: ${error}`);
        }

        return await response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        
        // Handle timeout/abort errors
        if (error instanceof Error && error.name === 'AbortError') {
          if (attempt < maxRetries) {
            const delay = 1000 * (attempt + 1); // Linear backoff: 1s, 2s, 3s
            console.warn(`⚠️ [ProjectFoldersService] Request timed out (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          throw new Error(`Folder creation timed out after ${maxRetries + 1} attempts (${(baseTimeout + maxRetries * 60000) / 1000}s total). The folders may still be creating in Google Drive. Please wait 2-3 minutes and refresh the page to check if they were created successfully. If not, try again.`);
        }
        
        // Re-throw non-retryable errors
        throw error;
      }
    }
    
    // This should never be reached, but TypeScript needs it
    throw new Error('Failed to create project folders after all retry attempts');
  }

  /**
   * Live progress for an in-flight folder creation. Returns `done: true`
   * when there's nothing in flight (either not yet started, finished
   * cleanly, or the server's in-memory tracker was lost). The
   * AddProjectForm polls this every ~1s while createProjectFolders is
   * pending so it can show "Creating Operations folders… (32 created)"
   * instead of a silent 60-120s spinner.
   */
  async getCreateProgress(
    projectId: string,
  ): Promise<
    | { done: true }
    | { done: false; created: number; section: string; elapsedMs: number }
  > {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/create-progress`,
      { headers: await getAuthHeaders() },
    );
    // Don't throw on transient errors — the caller is polling, the next
    // tick will recover. Treat any non-200 as "no progress info" and keep
    // showing the last-known label.
    if (!response.ok) return { done: true };
    return response.json();
  }

  /**
   * Get folder structure for a project
   * The normal lookup is intentionally database-only. Google Drive recovery is
   * an explicit Documents-screen action so opening Dashboard/Pricing never
   * starts a slow Drive scan during an Edge Function cold start.
   */
  async getProjectFolders(
    projectId: string,
    _userId?: string,
    recoverMissing = false,
  ): Promise<{ success: boolean; folders: ProjectFolders | null; exists: boolean; autoSynced?: boolean }> {
    const params = new URLSearchParams();
    if (recoverMissing) {
      params.set('recoverMissing', 'true');
    }
    const query = params.toString();
    const url = `${API_BASE}/project-folders/${projectId}${query ? `?${query}` : ''}`;
    const maxRetries = 2;
    const baseTimeout = 20000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const controller = new AbortController();
      const timeout = baseTimeout + attempt * 5000;
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        const response = await fetch(url, {
          headers: await getAuthHeaders(),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const error = await response.text();
          const isRetryable = response.status >= 500 || response.status === 429;

          if (isRetryable && attempt < maxRetries) {
            const delay = 750 * (attempt + 1);
            console.warn(
              `⚠️ [ProjectFoldersService] getProjectFolders retry ${attempt + 1}/${maxRetries + 1} in ${delay}ms`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          console.error(`Failed to get project folders:`, error);
          throw new Error(`Failed to get project folders: ${error}`);
        }

        const result = await response.json();
        return result;
      } catch (error) {
        clearTimeout(timeoutId);

        const isAbort =
          error instanceof Error &&
          (error.name === 'AbortError' || error.message.includes('signal is aborted'));
        const isNetworkFailure =
          error instanceof TypeError ||
          (error instanceof Error && error.message.includes('Failed to fetch'));

        if ((isAbort || isNetworkFailure) && attempt < maxRetries) {
          const delay = 750 * (attempt + 1);
          console.warn(
            `⚠️ [ProjectFoldersService] getProjectFolders network retry ${attempt + 1}/${maxRetries + 1} in ${delay}ms`,
            error
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        if (isAbort) {
          throw new Error('Failed to get project folders: request timed out');
        }

        if (isNetworkFailure) {
          throw new Error('Failed to get project folders: network request failed');
        }

        throw error;
      }
    }

    throw new Error('Failed to get project folders after retrying');
  }

  /**
   * List files in a specific project folder
   */
  async listFiles(
    projectId: string,
    folderType: keyof ProjectFolders,
    userId: string
  ): Promise<Array<{
    id: string;
    name: string;
    mimeType: string;
    size: number;
    createdTime: string;
    modifiedTime: string;
    webViewLink: string;
    webContentLink?: string;
  }>> {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/files/${folderType}?userId=${userId}`,
      {
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to list files';
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.files;
  }

  /** Read before presenting an editor; a list snapshot is not a reviewed baseline. */
  async getFileMetadata(projectId: string, fileId: string, signal?: AbortSignal, isCurrent: () => boolean = () => true): Promise<ReviewedFileMetadata> {
    const headers = await getAuthHeaders();
    if (signal?.aborted || !isCurrent()) throw new Error('Document changed; reopen before saving');
    const response = await fetch(`${API_BASE}/project-folders/file-metadata/${encodeURIComponent(fileId)}?projectId=${encodeURIComponent(projectId)}`, {
      headers, signal,
    });
    const data = await response.json();
    if (signal?.aborted || !isCurrent()) throw new Error('Document changed; reopen before saving');
    if (!response.ok) throw new Error(data.error || 'Could not load document metadata');
    if (data.file?.id !== fileId) throw new Error('Document identity changed; reload before editing');
    return data;
  }

  /** Category actions act on the file revision actually displayed by the picker. */
  async updateReviewedCategory(userId: string, projectId: string,
    file: Pick<GoogleDriveFile, 'id' | 'modifiedTime' | 'metadataRevision'>,
    next: string | null, isCurrent: () => boolean = () => true, signal?: AbortSignal,
    options: {allowInitialEnrollment?: boolean} = {}) {
    const baseline = await this.getFileMetadata(projectId, file.id, signal, isCurrent);
    if (signal?.aborted || !isCurrent()) throw new Error('Document changed; reopen before saving');
    if (!file.id.startsWith('native-') && !file.metadataRevision && baseline.metadataRevision && !options.allowInitialEnrollment) {
      throw new MetadataBaselineRefreshRequiredError({ ...baseline.file, metadataRevision: baseline.metadataRevision, projectionState: baseline.projectionState });
    }
    // Continue's automatic lane may classify a just-enrolled file from this
    // fresh baseline. Existing displayed revisions still require exact CAS.
    const reviewedRevision = !file.metadataRevision && options.allowInitialEnrollment
      ? baseline.metadataRevision : file.metadataRevision;
    if (file.id.startsWith('native-') ? baseline.file.modifiedTime !== file.modifiedTime
      : (!reviewedRevision || baseline.metadataRevision !== reviewedRevision)) {
      throw new Error('Document changed; reload this folder before changing its category.');
    }
    return this.updateFileMetadata(userId, file.id, next ? { category: next } : {}, undefined,
      { projectId, baseline, clearFields: next ? [] : ['category'], isCurrent, signal });
  }

  /** Submit the original reviewed revision exactly once. Never retry an unknown outcome. */
  async updateFileMetadata(
    userId: string,
    fileId: string,
    documentControlMetadata: DocumentControlMetadataInput,
    folderId: string | undefined,
    options: { projectId: string; baseline: ReviewedFileMetadata; clearFields?: string[]; isCurrent?: () => boolean; signal?: AbortSignal },
  ): Promise<{ file: GoogleDriveFile; metadataRevision?: string; projectionState?: MetadataProjectionState }> {
    const { baseline, projectId, clearFields = [], isCurrent = () => true, signal } = options;
    const native = fileId.startsWith('native-');
    if (baseline.file.id !== fileId || !baseline.supportsMetadataClearing || !baseline.supportsConditionalMetadataUpdate ||
      (!native && (baseline.canEdit !== true || baseline.metadataContractVersion !== 1 || !baseline.metadataRevision))) {
      throw new Error('Document editing is unavailable; reload before editing');
    }
    const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
    if (signal?.aborted || !isCurrent()) throw new Error('Document changed; reopen before saving');
    const commandId = crypto.randomUUID();
    let response: Response;
    try {
      response = await fetch(`${API_BASE}/project-folders/update-metadata`, {
        method: 'POST', headers, signal,
        body: JSON.stringify({ userId, fileId, folderId, projectId, documentControlMetadata, clearFields,
          ...(native ? { expectedModifiedTime: baseline.file.modifiedTime } : { expectedMetadataRevision: baseline.metadataRevision, commandId }),
        }),
      });
    } catch {
      throw new MetadataOutcomeUnknownError('Save outcome is unknown. Reload this document before saving again.');
    }
    let data;
    try { data = await response.json(); }
    catch { throw new MetadataOutcomeUnknownError('Save outcome is unknown. Reload this document before saving again.'); }
    if (!response.ok) {
      // A server failure can arrive after the canonical transaction committed.
      if (response.status >= 500) throw new MetadataOutcomeUnknownError('Save outcome is unknown. Reload this document before saving again.');
      throw new Error(data.error || 'Failed to update file metadata');
    }
    if (data.file?.id !== fileId || (!native && (!data.metadataRevision || data.commandId !== commandId || !['pending', 'synced', 'blocked'].includes(data.projectionState)))) {
      throw new MetadataOutcomeUnknownError('Save acknowledgement was incomplete. Reload this document before saving again.');
    }
    const metadata = data.file.documentControl || {};
    if (Object.entries(documentControlMetadata).some(([key, value]) => metadata[key] !== value) ||
      clearFields.some(key => metadata[key] != null && metadata[key] !== '')) {
      throw new MetadataOutcomeUnknownError('Saved metadata could not be confirmed. Reload this document before saving again.');
    }
    return { ...data, file: { ...data.file, documentControl: data.file.documentControl || {}, appProperties: data.file.appProperties || {},
      metadataRevision: data.metadataRevision || data.file.metadataRevision, projectionState: data.projectionState || data.file.projectionState } };
  }

  /**
   * Migrate all existing projects to create folder structures
   */
  async migrateAllProjects(userId: string): Promise<{
    success: boolean;
    results: {
      total: number;
      created: number;
      skipped: number;
      errors: string[];
      clientsCreated?: number;
    };
  }> {
    console.log('🔄 Calling migration endpoint:', `${API_BASE}/project-folders/migrate`);
    console.log('📤 Request body:', { userId });
    
    let response;
    try {
      response = await fetch(`${API_BASE}/project-folders/migrate`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ userId }),
      });
    } catch (fetchError) {
      console.error('❌ Fetch failed completely:', fetchError);
      throw new Error(`Network error: Cannot connect to server. The backend may be redeploying. Please wait 30-60 seconds and try again.`);
    }

    console.log('📥 Response status:', response.status, response.statusText);

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        console.error('❌ Error response:', errorText);
      } catch (e) {
        errorText = 'Could not read error response';
      }
      
      throw new Error(`Failed to migrate projects (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Migration response:', result);
    return result;
  }

  /**
   * Alias for migrateAllProjects
   */
  async migrateExistingProjects(userId: string): Promise<{
    success: boolean;
    results: {
      total: number;
      created: number;
      skipped: number;
      errors: string[];
      clientsCreated?: number;
    };
  }> {
    return this.migrateAllProjects(userId);
  }

  /**
   * Reorganize existing project folders into client hierarchy
   */
  async reorganizeExistingProjects(userId: string): Promise<{
    success: boolean;
    results: {
      total: number;
      moved: number;
      skipped: number;
      errors: string[];
    };
  }> {
    const response = await fetch(`${API_BASE}/project-folders/reorganize`, {
      method: 'POST',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('❌ Reorganization failed:', error);
      throw new Error(`Failed to reorganize projects: ${error}`);
    }

    const result = await response.json();
    return result;
  }

  /**
   * Upgrade existing projects from old structure to new comprehensive structure
   * This processes projects in batches to avoid timeout issues
   * 
   * @param userId - The user performing the upgrade
   * @param projectId - Optional: Upgrade a specific project only
   * @param batchSize - Optional: Number of projects to process (default 5, max 10)
   */
  async upgradeProjectStructure(
    userId: string, 
    projectId?: string,
    batchSize?: number
  ): Promise<{
    success: boolean;
    results: {
      total: number;
      needingUpgrade: number;
      upgraded: number;
      skipped: number;
      remaining: number;
      errors: string[];
    };
    hasMore: boolean;
    message: string;
  }> {
    try {
      const requestBody: any = { userId };
      if (projectId) requestBody.projectId = projectId;
      if (batchSize) requestBody.batchSize = batchSize;

      const response = await fetch(`${API_BASE}/project-folders/upgrade-structure`, {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(`Failed to upgrade project structure: ${errorJson.error || errorJson.message || errorText}`);
        } catch {
          throw new Error(`Failed to upgrade project structure (${response.status}): ${errorText}`);
        }
      }

      const result = await response.json();
      console.log('✅ Project structure upgrade batch completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Fetch error:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Network error: Cannot connect to server. The endpoint may not exist or CORS is blocking the request.');
      }
      throw error;
    }
  }

  /**
   * Upgrade all projects in batches, calling the API multiple times if needed
   * This is a convenience method that handles the batching automatically
   */
  async upgradeAllProjectsInBatches(
    userId: string,
    onProgress?: (upgraded: number, total: number, remaining: number) => void
  ): Promise<{
    totalUpgraded: number;
    totalSkipped: number;
    allErrors: string[];
  }> {
    let totalUpgraded = 0;
    let totalSkipped = 0;
    const allErrors: string[] = [];
    let hasMore = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    while (hasMore) {
      try {
        const result = await this.upgradeProjectStructure(userId, undefined, 5);
        
        // Reset retry count on success
        retryCount = 0;
        
        totalUpgraded += result.results.upgraded;
        totalSkipped += result.results.skipped;
        allErrors.push(...result.results.errors);
        
        hasMore = result.hasMore;
        
        if (onProgress) {
          onProgress(
            totalUpgraded, 
            result.results.total,
            result.results.remaining
          );
        }

        // Small delay between batches to avoid rate limiting
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error('❌ Error during batch upgrade:', error);
        
        // Check if it's a network error and we can retry
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const isNetworkError = errorMessage.includes('Network error') || errorMessage.includes('Failed to fetch');
        
        if (isNetworkError && retryCount < MAX_RETRIES) {
          retryCount++;
          const waitTime = Math.pow(2, retryCount) * 1000; // Exponential backoff: 2s, 4s, 8s
          console.warn(`⚠️  Network error, retrying in ${waitTime / 1000}s... (attempt ${retryCount}/${MAX_RETRIES})`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          // Don't increment hasMore, try again
          continue;
        }
        
        allErrors.push(errorMessage);
        hasMore = false; // Stop on non-retryable error or max retries reached
      }
    }

    return {
      totalUpgraded,
      totalSkipped,
      allErrors
    };
  }

  /**
   * Sync Google Drive folder permissions for a team member
   */
  async syncMemberPermissions(
    projectId: string,
    userId: string,
    userEmail: string,
    userRole: string,
    action: 'add' | 'remove',
    performedBy: string
  ): Promise<{ success: boolean; message: string; details?: any }> {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/sync-member-permissions`,
      {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          userEmail,
          userRole,
          action,
          performedBy,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to sync member permissions: ${error}`);
    }

    return await response.json();
  }

  /**
   * Sync an existing project's Google Drive folders to the database
   */
  async syncExistingProject(
    projectId: string,
    projectName: string,
    userId: string,
    rootFolderId: string,
    clientId?: string
  ): Promise<{ success: boolean; message: string; folders?: any; alreadyExists?: boolean }> {
    const response = await fetch(
      `${API_BASE}/project-folders/sync-existing`,
      {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          projectId,
          projectName,
          clientId,
          rootFolderId,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to sync existing project: ${error}`);
    }

    return await response.json();
  }

  /**
   * Sync Google Drive folder permissions for all team members
   */
  async syncAllPermissions(
    projectId: string,
    userId: string,
    teamMembers: Array<{ email: string; role: string }>,
    performedBy: string
  ): Promise<{ success: boolean; message: string; details?: any }> {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/sync-all-permissions`,
      {
        method: 'POST',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          teamMembers,
          performedBy,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to sync all permissions: ${error}`);
    }

    return await response.json();
  }

  /**
   * Get folder access log for a project
   */
  async getAccessLog(projectId: string): Promise<Array<{
    id: string;
    project_id: string;
    folder_id: string;
    user_email: string;
    user_role: string;
    permission_level: string;
    action: string;
    performed_at: string;
    performed_by: string;
  }>> {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/access-log`,
      {
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get access log: ${error}`);
    }

    const data = await response.json();
    return data.accessLog;
  }

  /**
   * Get visible folders for a user based on their role
   */
  async getVisibleFolders(
    projectId: string,
    userRole: string
  ): Promise<{ success: boolean; folders: any; fullStructure: any }> {
    const response = await fetch(
      `${API_BASE}/project-folders/${projectId}/visible-folders?userRole=${encodeURIComponent(userRole)}`,
      {
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get visible folders: ${error}`);
    }

    return await response.json();
  }
}
